<template>
  <TableList
    v-rect-loading="loading"
    class="hm-management-list"
    row-key="id"
    v-if="showTable"
    :title="$t(`${i18nPrefix}.title.hmManagement`)"
    :listData="hmList"
    :tableColumns="tableColumns"
    :actionBtn="actionBtn"
    :showCreateBtn="false"
    :pagination="pagination"
    :defaultSort="defaultOrder"
    @pagination-change="handlePaginationChange"
    @sort-change="handleSortChange"
  >
    <template #filter>
      <div class="header-left">
        <span class="text">{{ $t(`${i18nPrefix}.label.companyName`) }}</span>
        <span class="company-select">
          <el-select v-model="selectCompany" filterable clearable @change="handleCompanySelect">
            <el-option
              v-for="item in companyList"
              :key="item.id"
              :label="item.i18nName"
              :style="{ paddingLeft: item.level * 15 + 'px' }"
              :value="item.id"
            >
            </el-option>
          </el-select>
        </span>
      </div>
      <div class="header-left">
        <span class="text">{{ $t(`${i18nPrefix}.label.activeMonth`) }}</span>
        <span class="company-select">
          <el-select v-model="lastActiveMonth" filterable @change="handleActiveSelect">
            <el-option
              v-for="item in lastActiveMonthList"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            >
            </el-option>
          </el-select>
        </span>
      </div>
    </template>
    <template #btn>
      <el-button v-if="hasSatisfactionSurveyCandidate" class="blue-btn" @click="handelSurvey">{{ $t(`${i18nPrefix}.btn.survey`) }}</el-button>
      <el-button class="blue-btn" @click="handelAddHM">{{ $t(`${i18nPrefix}.btn.addHM`) }}</el-button>
    </template>
    <template #checkHeader>
      <el-checkbox :value="isCheckedAll" :indeterminate="indeterminate" @change="handleCheckAll"></el-checkbox>
    </template>
    <template #check="{ data, row }">
      <el-checkbox :value="data" @change="handleCheckOne($event, row)"></el-checkbox>
    </template>
    <template #status="{ data, row }">
      <el-switch :value="data === 1" @change="onEnabledChange($event, row)"></el-switch>
    </template>
    <template #active="{ data }">
      <el-switch :value="data === 1" disabled></el-switch>
    </template>
    <template #page-bottom>
      <DialogWrapper
        saveBtnLabelI18nKey="common.btn.confirm"
        closeOnCancel
        :title="$t(`${i18nPrefix}.message.sendSurvey`)"
        :loading="surveyDialog.loading"
        :show.sync="surveyDialog.show"
        @save="handleSendSurvey"
      >
        <WrappedFrom
          ref="form"
          class="survey-form"
          :model="surveyData"
          :formLines="surveyFormLines"
        />
      </DialogWrapper>
    </template>
  </TableList>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { addI18nProperty, isEn } from '@/i18n'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import moment from 'moment'

export default {
  components: {
    TableList,
    DialogWrapper,
    WrappedFrom
  },
  data () {
    return {
      i18nPrefix: 'admin.company.hmManagement',
      loading: false,
      isEn,
      hmList: [],
      companyList: [],
      questionnaireList: [],
      emailTemplateList: [],
      surveyDialog: {
        loading: false,
        show: false
      },
      showTable: true,
      search: {},
      surveyData: {},
      selectCompany: null,
      rootCompany: null,
      lastActiveMonth: 1,
      queryLastActiveMonth: 1,
      isFirst: true,
      actionBtn: {
        edit: {
          show: true,
          onclick: this.handleEditHM
        }
      },
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      order: '-is_enable',
      totalCount: 0,
      isCheckedAll: false,
      indeterminate: false,
      currentCompanyId: null
    }
  },
  computed: {
    hasSatisfactionSurveyCandidate () {
      return this.hasAnyPermission(['PREMIUM_SATISFACTION_SURVEY_CANDIDATE', 'PERM_SATISFACTION_SURVEY_MANAGE'])
    },
    lastActiveMonthList () {
      return [
        { id: 1, name: this.$t(`${this.i18nPrefix}.label.oneMonth`) },
        { id: 2, name: this.$t(`${this.i18nPrefix}.label.twoMonth`) },
        { id: 3, name: this.$t(`${this.i18nPrefix}.label.threeMonth`) },
        { id: 4, name: this.$t(`${this.i18nPrefix}.label.fourMonth`) },
        { id: 5, name: this.$t(`${this.i18nPrefix}.label.fiveMonth`) },
        { id: 6, name: this.$t(`${this.i18nPrefix}.label.sixMonth`) }
      ]
    },
    defaultOrder () {
      const order = this.order
      if (order.includes('-')) {
        return {
          prop: order.split('-')[1],
          order: 'descending'
        }
      } else {
        return {
          prop: order,
          order: 'ascending'
        }
      }
    },
    tableColumns () {
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.checkAll`),
          dataKey: 'isChecked',
          slotName: 'check',
          headerSlotName: 'checkHeader',
          width: '34px',
          cellClass: 'overflow'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.firstName`),
          dataKey: 'first_name',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.lastName`),
          dataKey: 'last_name',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.company`),
          dataKey: 'companyName',
          showOverflowTooltip: true,
          width: '160px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.email`),
          dataKey: 'email_address',
          showOverflowTooltip: true,
          width: '120px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.status`),
          dataKey: 'is_enable',
          slotName: 'status',
          sort: {
            enable: 'custom',
            orders: ['descending', 'ascending']
          }
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.active`),
          dataKey: 'is_active',
          slotName: 'active',
          disabled: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.updateTime`),
          dataKey: 'updated_time',
          sort: {
            enable: 'custom',
            orders: ['descending', 'ascending']
          },
          showOverflowTooltip: true,
          formatter (data) {
            return moment(data).format('YYYY-MM-DD')
          }
        }
      ]
      return columns
    },
    surveyFormLines () {
      const lines = {
        questionnaire_id: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.questionnaire`),
          option: {
            data: this.questionnaireList,
            valueKey: 'id',
            labelKey: 'name'
          },
          props: {
            clearable: true,
            filterable: true
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.questionnaire`),
          rules: [{ required: true, message: this.$t(`${this.i18nPrefix}.placeholder.questionnaire`), trigger: 'blur' }]
        },
        email_template_id: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.emailTemplate`),
          option: {
            data: this.emailTemplateList,
            valueKey: 'id',
            labelKey: 'subject'
          },
          props: {
            clearable: true,
            filterable: true
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.emailTemplate`),
          rules: [{ required: true, message: this.$t(`${this.i18nPrefix}.placeholder.emailTemplate`), trigger: 'blur' }]
        }
      }
      return lines
    }
  },
  created () {
    this.getOrderFromUrl()
    this.initData()
  },
  methods: {
    ...mapActions('hm', ['getCompanyHmList', 'updateCompanyHm']),
    ...mapActions('company', ['getCompanyChildrenTree']),
    ...mapActions('satisfactionSurveyManagement', ['searchQuestionnaire', 'searchEmailTemplate', 'createInvitation']),
    async initData () {
      this.loading = true
      try {
        await this.loadCompanyList()
        this.getCompanyFromUrl()
        this.handleRouteChange()
        await this.loadCompanyHMList()
      } finally {
        this.loading = false
        this.isFirst = false
      }
    },
    async loadCompanyList () {
      const companyTreeResp = await this.getCompanyChildrenTree()
      const currentCompany = {
        id: companyTreeResp.self.id,
        name: companyTreeResp.self.name,
        cn_name: companyTreeResp.self.cn_name,
        level: companyTreeResp.self.level
      }
      this.selectCompany = currentCompany.id
      this.currentCompanyId = currentCompany.id
      this.rootCompany = companyTreeResp.root?.id
      const companyList = []
      this.companyList = this.formatCompanyData(companyTreeResp, currentCompany, companyList)
    },
    async loadCompanyHMList (reload) {
      if (reload) {
        this.pagination.page = 1
        this.pagination.total = 0
      }
      this.loading = true
      try {
        const params = {
          company_id: this.selectCompany,
          page: this.pagination.page,
          page_size: this.pagination.pageSize,
          last_active_month: this.queryLastActiveMonth,
          order: this.order
        }
        const resp = await this.getCompanyHmList(params)
        _.forEach(resp.hm_list, hm => {
          addI18nProperty(hm, 'companyName', 'company.cn_name', 'company.name')
          hm.isChecked = false
        })
        this.hmList = resp.hm_list
        this.pagination.total = resp.total_count
      } finally {
        this.loading = false
      }
    },
    handlePaginationChange () {
      this.handleCheckAll(false)
      this.loadCompanyHMList()
    },
    handleSortChange (prop, order) {
      this.order = `${order}${prop}`
      this.handleRouteChange(true)
    },
    handleSelect () {
      this.handleRouteChange(true)
    },
    handleActiveSelect () {
      this.queryLastActiveMonth = this.lastActiveMonth
      this.handleSelect()
    },
    handleCompanySelect () {
      this.handleSelect()
    },
    async onEnabledChange (status, row, column) {
      this.loading = true
      try {
        const data = {
          status: status ? 1 : 0
        }
        const resp = await this.updateCompanyHm({ companyHmId: row.id, data })
        if (resp) {
          row.is_enable = resp.status
          this.$message.success(this.$t(`${this.i18nPrefix}.message.optionSuccess`))
        } else {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.optionField`))
        }
      } finally {
        this.loading = false
      }
    },
    formatCompanyData (data, company, companyList) {
      addI18nProperty(company, 'i18nName', 'cn_name', 'name')
      companyList.push(company)
      const allChildren = data.children
      const children = allChildren[company.id]
      if (children && children.length > 0) {
        _.forEach(children, (child) => {
          this.formatCompanyData(data, child, companyList)
        })
      }
      return companyList
    },
    handelAddHM () {
      this.$router.push({
        path: '/v-admin/hm/management/add',
        query: {
          company: this.selectCompany || this.currentCompanyId,
          active_month: this.queryLastActiveMonth
        }
      })
    },
    handleEditHM (row) {
      this.$router.push({
        path: `/v-admin/hm/management/edit/${row.id}`,
        query: {
          company: row.company.id,
          active_month: this.queryLastActiveMonth
        }
      })
    },
    async handelSurvey () {
      if (!this.indeterminate && !this.isCheckedAll) {
        this.$message.error(this.$t(`${this.i18nPrefix}.message.checkedLeastOneHm`))
        return
      }
      this.surveyDialog.show = true
      this.surveyDialog.loading = true
      this.surveyData = {}
      try {
        const promiseList = []
        let params = {
          just_active_list: true,
          only_candidate: true
        }
        promiseList.push(this.searchQuestionnaire(params))
        params = {
          only_general: true
        }
        promiseList.push(this.searchEmailTemplate(params))
        const resp = await Promise.all(promiseList)
        if (resp) {
          this.questionnaireList = resp[0].questionnaires
          this.emailTemplateList = resp[1].email_template
        }
      } finally {
        this.surveyDialog.loading = false
      }
    },
    async handleSendSurvey () {
      const result = await this.$refs.form.submit()
      if (!result) {
        return
      }
      const hmIds = []
      _.forEach(this.hmList, item => {
        if (item.isChecked) {
          hmIds.push(item.id)
        }
      })
      const data = {
        questionnaire_id: this.surveyData.questionnaire_id,
        ids: hmIds,
        email_template_id: this.surveyData.email_template_id,
        invitation_target: 'HM'
      }
      this.surveyDialog.loading = true
      try {
        const resp = await this.createInvitation(data)
        if (resp) {
          this.$message.success(resp.message)
          this.surveyDialog.show = false
        }
      } finally {
        this.surveyDialog.loading = false
      }
    },
    handleClickCheckbox (event) {
      event.preventDefault()
    },
    handleCheckAll (value) {
      this.isCheckedAll = value
      if (value) {
        _.forEach(this.hmList, item => {
          item.isChecked = true
        })
      } else {
        _.forEach(this.hmList, item => {
          item.isChecked = false
        })
      }
      this.indeterminate = false
    },
    handleCheckOne (value, row) {
      row.isChecked = value
      let count = 0
      _.forEach(this.hmList, item => {
        if (item.isChecked) {
          count++
        }
      })
      if (count > 0 && count < this.hmList.length) {
        this.indeterminate = true
      } else {
        this.indeterminate = false
      }
      if (count === this.hmList.length) {
        this.isCheckedAll = true
      } else {
        this.isCheckedAll = false
      }
    },
    getOrderFromUrl () {
      const order = this.$route.query.order
      const activeMonth = this.$route.query.active_month
      if (order) {
        this.order = order
      } else {
        this.order = '-is_enable'
      }
      if (activeMonth) {
        this.queryLastActiveMonth = parseInt(activeMonth)
        this.lastActiveMonth = this.queryLastActiveMonth
      } else {
        this.queryLastActiveMonth = 1
      }
    },
    getCompanyFromUrl () {
      const selectCompany = parseInt(this.$route.query.company)
      if (selectCompany) {
        this.selectCompany = selectCompany
      }
    },
    handleRouteChange (isPush) {
      const selectCompany = this.$route.query.company
      const order = this.$route.query.order
      const activeMonth = this.$route.query.active_month

      if (((selectCompany || this.selectCompany) && parseInt(selectCompany) !== this.selectCompany) || (this.order && order !== this.order) || activeMonth !== `${this.queryLastActiveMonth}`) {
        isPush
          ? this.$router.push({
            query: {
              company: this.selectCompany,
              order: this.order,
              active_month: this.queryLastActiveMonth
            }
          })
          : this.$router.replace({
            query: {
              company: this.selectCompany,
              order: this.order,
              active_month: this.queryLastActiveMonth
            }
          })
      }
    }
  },
  watch: {
    $route () {
      this.showTable = false
      this.$nextTick(() => {
        this.showTable = true
        this.getOrderFromUrl()
        this.getCompanyFromUrl()
        this.handleRouteChange()
        if (!this.isFirst) {
          this.loadCompanyHMList(true)
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.hm-management-list {

  ::v-deep {

    .overflow {

      .cell {
        overflow: visible;
      }
    }
  }

  .statistics {
    display: inline-block;

    .success-icon {
      font-size: 20px;
      color: #0096cc;
      vertical-align: middle;
      margin-right: 5px;
    }

    .error-icon {
      font-size: 17px;
      color: #aaa;
      vertical-align: middle;
      margin-right: 5px;
    }

    .statistics-count {
      border-right: 1px solid #aaa;
      margin-right: 10px;
      padding-right: 10px;
      vertical-align: middle;
    }
  }
  .header-left {
    display: inline-block;
    vertical-align: middle;
    .text {
      margin-right: 10px;
      display: inline-block;
      vertical-align: middle;
    }

    .company-select {
      display: inline-block;
      vertical-align: middle;
    }
  }

  ::v-deep {

    .list-head {
      height: auto;
      flex-wrap: wrap;
      justify-content: flex-end;
      margin-bottom: 10px;

      .title {
        margin-bottom: 10px;
        flex-shrink: 0;
      }

      .filter-container {
        margin-bottom: 10px;
        flex-shrink: 0;
      }

      .btn-container {
        margin-bottom: 10px;
        word-break: keep-all;
        white-space: nowrap;
      }

    }

    .el-checkbox__label {
      color: #909699;
    }

  }

  .survey-form {
  }
}
</style>
